/**
 * AgentBuilder.tsx — Modern AI Workspace UI
 * Logic layer: all state, API calls, and effects preserved exactly.
 * Presentation: delegated to agentWorkspace/* components.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { customAgentAPI, mcpAPI, onboardingAPI, agentRunAPI, skillsAPI } from '../services/api.js';
import { useAI } from '../context/AIContext.jsx';
import toast from 'react-hot-toast';
import ConfirmScheduledRunModal from '../components/modals/ConfirmScheduledRunModal.tsx';

// ── Workspace components ────────────────────────────────────────────────────
import WorkspaceLayout         from '../components/agentWorkspace/WorkspaceLayout.tsx';
import SkillLibrarySidebar     from '../components/agentWorkspace/SkillLibrarySidebar.tsx';
import AgentWorkspaceCenter    from '../components/agentWorkspace/AgentWorkspaceCenter.tsx';
import RuntimeConfigPanel      from '../components/agentWorkspace/RuntimeConfigPanel.tsx';
import CommandPalette          from '../components/agentWorkspace/CommandPalette.tsx';
import { WS, WORKSPACE_CSS, SKILL_LIBRARY } from '../components/agentWorkspace/workspaceData.ts';

// ── Main component ─────────────────────────────────────────────────────────
export default function AgentBuilder() {
  const { agentId } = useParams<{ agentId: string }>();
  const location    = useLocation();
  const navigate    = useNavigate();
  const { models, selectedModelId, selectedMode }  = useAI();

  const isNew   = agentId === 'new';
  const prefill = (location.state as any)?.prefill;

  // Skill Library sidebar: collapsed by default for a brand-new agent (an
  // empty skill list has nothing to show yet), expanded when editing an
  // existing agent so its already-selected skills stay visible.
  const [leftCollapsed, setLeftCollapsed] = useState(isNew);

  // ── State (all preserved from original) ──────────────────────────────────
  const [name,              setName]              = useState('');
  const [description,       setDescription]       = useState('');
  const [domain,            setDomain]            = useState('general');
  const [defaultModel,      setDefaultModel]      = useState('');
  const [personality,       setPersonality]       = useState('');
  const [icon,              setIcon]              = useState('🤖');
  const [enabled,           setEnabled]           = useState(true);
  const [goal,              setGoal]              = useState('');
  const [systemPrompt,      setSystemPrompt]      = useState('');
  const [selectedTools,     setSelectedTools]     = useState<string[]>([]);
  const [memoryEnabled,     setMemoryEnabled]     = useState(false);
  const [contextNote,       setContextNote]       = useState('');
  const [temperature,       setTemperature]       = useState(0.7);
  const [maxTokens,         setMaxTokens]         = useState(2048);
  const [schedule,          setSchedule]          = useState('');
  const [initialSchedule,   setInitialSchedule]   = useState('');
  const [scheduledModalOpen, setScheduledModalOpen] = useState(false);
  const [scheduledConflictRun, setScheduledConflictRun] = useState<any | null>(null);
  const [pendingSchedule, setPendingSchedule] = useState<any | null>(null);
  const [examples,          setExamples]          = useState<string[]>([]);
  const [inputHint,         setInputHint]         = useState('');
  const [requiredInputType, setRequiredInputType] = useState<string>('');
  const [loading,           setLoading]           = useState(!isNew);
  const [saving,            setSaving]            = useState(false);
  const [saved,             setSaved]             = useState(false);
  const [serverNameConflict, setServerNameConflict] = useState<string | null>(null);
  const [mcpServers, setMcpServers] = useState<any[]>([]);
  const [selectedMcpIds, setSelectedMcpIds] = useState<string[]>([]);
  const [allowedDomains, setAllowedDomains] = useState<string[] | null>(null);
  const [mcpEnabled,     setMcpEnabled]     = useState(false);
  const [inputSchema,    setInputSchema]    = useState('');
  const [isLyran, setIsLyran] = useState(false);
  const [cadence,        setCadence]        = useState<'one_shot' | 'recurring'>('one_shot');
  const [recurrenceRule, setRecurrenceRule] = useState('');
  const [timezone,       setTimezone]       = useState('UTC');
  const [notifyOnCompletion,   setNotifyOnCompletion]   = useState(false);
  const [notificationChannels, setNotificationChannels] = useState<string[]>([]);

  // ── Command palette state ─────────────────────────────────────────────────
  const [cmdOpen, setCmdOpen] = useState(false);

  // ── ⌘K keyboard shortcut ─────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdOpen(v => !v);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // ── Load MCP servers ──────────────────────────────────────────────────────
  useEffect(() => {
    mcpAPI.listServers()
      .then((r: any) => setMcpServers((r.servers || []).filter((s: any) => s.enabled)))
      .catch(() => {});
  }, []);

  // ── Load/prefill agent data ───────────────────────────────────────────────
  useEffect(() => {
    if (prefill) {
      setName(prefill.name || '');
      setDescription(prefill.description || '');
      setDefaultModel(prefill.model || prefill.defaultModel || '');
      setSystemPrompt(prefill.systemPrompt || '');
      setSelectedTools((prefill.skills || []).map((s: any) => typeof s === 'string' ? s : s.skillId));
      setPersonality(prefill.personality || '');
      setIcon(prefill.icon || '🤖');
      setExamples(prefill.examples || []);
      setGoal(prefill.goal || '');
      setTemperature(prefill.temperature ?? 0.7);
    }
    if (!isNew && agentId) {
      (async () => {
        try {
          const d = await customAgentAPI.list();
          const agents: any[] = Array.isArray(d) ? d : d?.agents || [];
          let agent = agents.find((a: any) => a._id === agentId || a.id === agentId);
          // If not found among CustomAgent list, try AgentDefinition endpoint
          if (!agent) {
            try {
              const resp = await fetch(`/api/agents/${agentId}`, { headers: { 'accept': 'application/json' } });
              if (resp.ok) {
                const json = await resp.json();
                agent = json.agent;
              }
            } catch (e) { /* ignore */ }
          }
          if (agent) {
          setIsLyran(!!agent.isLyran);
          setName(agent.name || '');
          setDescription(agent.description || '');
          setDomain(agent.category || 'general');
          setDefaultModel(agent.defaultModel || agent.model || '');
          setPersonality(agent.personality || '');
          setIcon(agent.icon || '🤖');
          setEnabled(agent.enabled !== false);
          setGoal(agent.goal || '');
          setSystemPrompt(agent.systemPrompt || '');
          setSelectedTools(agent.skills || []);
          setMemoryEnabled(!!agent.memoryEnabled);
          setContextNote(agent.context || '');
          setTemperature(agent.temperature ?? 0.7);
          setMaxTokens(agent.maxTokens || 2048);
          setSchedule(agent.schedule || '');
          setExamples(agent.examples || []);
          setSelectedMcpIds((agent.mcpServerIds || []).map((id: any) => id.toString()));
          // Normalize skills — may be stored as plain strings or { skillId, promptText } objects
          setSelectedTools((agent.skills || []).map((s: any) => typeof s === 'string' ? s : s.skillId));
          setInputHint(agent.inputHint || '');
          setRequiredInputType(agent.requiredInputType || '');
          setMcpEnabled(!!agent.mcpEnabled);
          setInputSchema(agent.inputSchema ? JSON.stringify(agent.inputSchema, null, 2) : '');
          setCadence(agent.cadence === 'recurring' ? 'recurring' : 'one_shot');
          setRecurrenceRule(agent.recurrenceRule || '');
          setTimezone(agent.timezone || 'UTC');
          setNotifyOnCompletion(!!agent.notifyOnCompletion);
          setNotificationChannels(agent.notificationChannels || []);
          setInitialSchedule(agent.schedule || '');
          // If there is a one-time scheduled run for this agent, prefer showing that in the datetime input
          try {
            const sr = await agentRunAPI.getScheduled();
            const runs = sr?.runs || [];
            const myRun = runs.find((r: any) => String(r.agentId) === String(agent._id));
            if (myRun && myRun.scheduledAt) {
              // Convert to `datetime-local` input value: YYYY-MM-DDTHH:mm
              const d = new Date(myRun.scheduledAt);
              const pad = (n: number) => String(n).padStart(2, '0');
              const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
              setSchedule(local);
              setInitialSchedule(local);
            }
          } catch (e) { /* ignore */ }
        }
        } catch (e) {
          // ignore errors
        } finally {
          setLoading(false);
        }
      })();
      
    }
  }, [agentId, isNew, prefill]);

  // ── Prefill baseline for change detection ─────────────────────────────────
  const prefillPrompt = (prefill?.systemPrompt || '').trim();
  const prefillSkills = (prefill?.skills || []).map((s: any) => typeof s === 'string' ? s : s.skillId).filter(Boolean);
  const normalizeArr = (arr: string[]) => Array.from(new Set((arr || []).map(a => String(a).trim()))).sort();
  const skillsEqual = (a: string[], b: string[]) => JSON.stringify(normalizeArr(a)) === JSON.stringify(normalizeArr(b));
  const promptChanged = systemPrompt.trim() !== prefillPrompt;
  const skillsChanged = !skillsEqual(selectedTools, prefillSkills);
  const canSave = !prefill ? true : (promptChanged || skillsChanged);

  // Load user onboarding domains to filter skill domains
  useEffect(() => {
    const fetch = async () => {
      try {
        const { onboarding } = await onboardingAPI.get();
        if (onboarding?.domains && onboarding.domains.length) setAllowedDomains(onboarding.domains);
      } catch (e) { /* ignore */ }
    };
    fetch();
  }, []);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!name.trim()) { toast.error('Agent name is required'); return; }
    if (notifyOnCompletion && notificationChannels.length === 0) {
      toast.error('Select at least one notify channel before saving'); return;
    }
    if (cadence === 'recurring' && !recurrenceRule) {
      toast.error('Pick a recurrence frequency before saving'); return;
    }
    setSaving(true);
    const payload: any = {
      name: name.trim(), description: description.trim(), category: domain, defaultModel,
      personality: personality.trim(), icon, enabled, goal: goal.trim(),
      systemPrompt: systemPrompt.trim(), skills: selectedTools.map(id => {
        const wsSkill = SKILL_LIBRARY.find(s => s.id === id);
        const promptText = wsSkill?.prompt;
        return promptText ? { skillId: id, promptText } : id;
      }), memoryEnabled,
      context: contextNote.trim(), temperature, maxTokens,
      schedule: schedule.trim(), examples: examples.filter(e => e.trim()),
      mcpServerIds: selectedMcpIds,
      inputHint: inputHint.trim(),
      requiredInputType: requiredInputType || null,
      mcpEnabled,
      inputSchema: (() => { try { return inputSchema.trim() ? JSON.parse(inputSchema) : null; } catch { return null; } })(),
      cadence,
      recurrenceRule: cadence === 'recurring' ? recurrenceRule : null,
      timezone: cadence === 'recurring' ? timezone : null,
      notifyOnCompletion,
      notificationChannels,
    };
    // If this workspace was opened as a clone/prefill, only allow saving when prompt or skills changed
    if (prefill && !canSave) {
      toast.error('Make a change to the prompt or skills before saving this cloned agent');
      setSaving(false);
      return;
    }
    // Attach parentAgentId when creating from a prefill (fork history)
    if (isNew && prefill && (prefill._id || prefill.id)) {
      payload.parentAgentId = prefill._id || prefill.id;
    }
    try {
      let res: any = null;
      if (isNew) { res = await customAgentAPI.create(payload); toast.success('Agent created!'); }
      else       { res = await customAgentAPI.update(agentId!, payload); toast.success('Agent saved!'); }

      // If a one-time schedule was added or changed via the builder, create a scheduled run
      const savedAgent = res?.agent || res?.agent || null;
      const savedAgentId = savedAgent?._id || savedAgent?.id || agentId;
      if (schedule && schedule.trim() && (isNew || (initialSchedule || '') !== schedule)) {
        try {
          // Check for existing scheduled runs for this user and agent
          const resp = await agentRunAPI.getScheduled();
          const runs = resp?.runs || [];
          const existing = runs.find((r: any) => String(r.agentId) === String(savedAgentId));
          if (existing) {
            // Show confirmation modal to let user choose
            setScheduledConflictRun(existing);
            setPendingSchedule({ agentId: savedAgentId, agentName: name.trim(), scheduledAt: new Date(schedule).toISOString() });
            setScheduledModalOpen(true);
          } else {
            const created = await agentRunAPI.scheduleRun({ agentId: savedAgentId, agentName: name.trim(), scheduledAt: new Date(schedule).toISOString() });
            // update local initial schedule to avoid duplicate scheduling on subsequent saves
            setInitialSchedule(schedule);
            // Notify other UI (Dashboard calendar/mission control) about the new scheduled run
            try { window.dispatchEvent(new CustomEvent('lyra:agent-scheduled', { detail: created.run || created })); } catch (e) {}
          }
        } catch (e) {
          // Non-fatal: scheduling failed, but agent saved successfully
          console.warn('Failed to create scheduled run:', e);
        }
      }
      setSaved(true);
      setTimeout(() => navigate('/agents'), 800);
    } catch (e: any) {
      // If server says lyran name conflict, surface a clear message
      const serverMsg = e?.response?.data?.error || e?.message || '';
      if (e?.response?.status === 409 || /lyran name taken|duplicate lyran/i.test(serverMsg)) {
        const msg = e?.response?.data?.error || 'Duplicate Lyran! not allowed..';
        // show inline pill by setting serverNameConflict and also show toast
        setServerNameConflict(msg);
        toast.error(msg);
      } else {
        toast.error('Failed to save agent');
      }
    } finally { setSaving(false); }
  }, [
    name, description, domain, defaultModel, personality, icon, enabled, goal,
    systemPrompt, selectedTools, memoryEnabled, contextNote, temperature,
    maxTokens, schedule, examples, isNew, agentId, navigate, selectedMcpIds,
    inputHint, requiredInputType, mcpEnabled, inputSchema,
    cadence, recurrenceRule, timezone, notifyOnCompletion, notificationChannels,
  ]);

  // ── Scheduled-run modal handlers
  const handleReplaceSchedule = async () => {
    if (!pendingSchedule || !scheduledConflictRun) return;
    try {
      await agentRunAPI.cancelScheduled(scheduledConflictRun._id);
      // notify removal
      try { window.dispatchEvent(new CustomEvent('lyra:agent-schedule-removed', { detail: { runId: scheduledConflictRun._id, agentId: scheduledConflictRun.agentId } })); } catch (e) {}
    } catch (e) {
      console.warn('Failed to cancel existing scheduled run', e);
    }
    try {
      const created = await agentRunAPI.scheduleRun(pendingSchedule);
      setInitialSchedule(pendingSchedule.scheduledAt);
      try { window.dispatchEvent(new CustomEvent('lyra:agent-scheduled', { detail: created.run || created })); } catch (e) {}
    } catch (e) {
      console.warn('Failed to create scheduled run after replace', e);
    }
    setPendingSchedule(null);
    setScheduledConflictRun(null);
    setScheduledModalOpen(false);
  };

  const handleKeepExisting = () => {
    if (scheduledConflictRun?.scheduledAt) {
      // convert ISO scheduledAt to datetime-local local string
      try {
        const d = new Date(scheduledConflictRun.scheduledAt);
        const pad = (n: number) => String(n).padStart(2, '0');
        const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        setInitialSchedule(local);
        setSchedule(local);
      } catch (e) {
        setInitialSchedule(scheduledConflictRun.scheduledAt);
      }
    }
    setPendingSchedule(null);
    setScheduledConflictRun(null);
    setScheduledModalOpen(false);
  };

  // ── Toggle skill ──────────────────────────────────────────────────────────
  const toggleTool = async (toolId: string) => {
    const adding = !(selectedTools || []).includes(toolId);
    // Optimistic update
    setSelectedTools(prev => prev.includes(toolId) ? prev.filter(id => id !== toolId) : [...prev, toolId]);

    // If this is a Lyra built-in that maps to a registry skill, install/uninstall on the server
    try {
      if (toolId === 'lyra:gmail-reader') {
        if (adding) await skillsAPI.install('gmail');
        else await skillsAPI.uninstall('gmail');
      }
      if (toolId === 'lyra:calendar-assistant') {
        if (adding) await skillsAPI.install('google-calendar');
        else await skillsAPI.uninstall('google-calendar');
      }
    } catch (e) {
      console.error('Failed to install/uninstall registry skill for', toolId, e);
      // rollback optimistic change on error
      setSelectedTools(prev => prev.includes(toolId) ? prev.filter(id => id !== toolId) : [...prev, toolId]);
    }
  };

  const removeSkill = (toolId: string) =>
    setSelectedTools(prev => prev.filter(id => id !== toolId));

  // ── Loading state ─────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: WS.bg, gap: 14,
      fontFamily: "'DM Sans', system-ui, sans-serif",
    }}>
      <style>{WORKSPACE_CSS}</style>
      <div style={{
        width: 48, height: 48, borderRadius: 14,
        background: WS.accentBg, border: `1px solid ${WS.accentBd}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Loader2 size={22} color={WS.accent} style={{ animation: 'ws-spin 1s linear infinite' }} />
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 500, color: WS.ink, textAlign: 'center', marginBottom: 4 }}>
          Loading agent…
        </div>
        <div style={{ fontSize: 12, color: WS.ink4, textAlign: 'center' }}>
          Fetching configuration
        </div>
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <WorkspaceLayout
        isNew={isNew}
        name={name}
        icon={icon}
        setIcon={setIcon}
        prefill={prefill}
        enabled={enabled}
        saving={saving}
        saved={saved}
        onSave={handleSave}
        canSave={canSave}
        onBack={() => navigate('/agents')}
        leftCollapsed={leftCollapsed}
        leftPanel={
          <SkillLibrarySidebar
            activeSkillIds={selectedTools}
            onToggleSkill={toggleTool}
            mcpServers={mcpServers}
            selectedMcpIds={selectedMcpIds}
            onToggleMcp={(id) => setSelectedMcpIds(prev =>
              prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
            )}
            allowedDomains={allowedDomains || undefined}
            collapsed={leftCollapsed}
            onToggleCollapsed={() => setLeftCollapsed(v => !v)}
          />
        }
        centerPanel={
          <AgentWorkspaceCenter
            name={name}                       setName={setName}
            isLyran={isLyran}
            description={description}         setDescription={setDescription}
            domain={domain}                   setDomain={setDomain}
            personality={personality}         setPersonality={setPersonality}
            goal={goal}                       setGoal={setGoal}
            inputHint={inputHint}             setInputHint={setInputHint}
            requiredInputType={requiredInputType} setRequiredInputType={setRequiredInputType}
            systemPrompt={systemPrompt}       setSystemPrompt={setSystemPrompt}
            activeSkillIds={selectedTools}
            onToggleSkill={toggleTool}
            onRemoveSkill={removeSkill}
            externalNameConflict={serverNameConflict || undefined}
            onNameEdit={() => { if (serverNameConflict) setServerNameConflict(null); }}
          />
        }
        rightPanel={
          <RuntimeConfigPanel
            models={models as any[] || []}
            selectedModelId={selectedModelId || undefined}
            selectedMode={selectedMode || 'auto'}
            defaultModel={defaultModel}       setDefaultModel={setDefaultModel}
            temperature={temperature}         setTemperature={setTemperature}
            maxTokens={maxTokens}             setMaxTokens={setMaxTokens}
            memoryEnabled={memoryEnabled}     setMemoryEnabled={setMemoryEnabled}
            contextNote={contextNote}         setContextNote={setContextNote}
            mcpServers={mcpServers}
            selectedMcpIds={selectedMcpIds}   setSelectedMcpIds={setSelectedMcpIds}
            schedule={schedule}               setSchedule={setSchedule}
            enabled={enabled}                 setEnabled={setEnabled}
            mcpEnabled={mcpEnabled}           setMcpEnabled={setMcpEnabled}
            inputSchema={inputSchema}         setInputSchema={setInputSchema}
            cadence={cadence}                 setCadence={setCadence}
            recurrenceRule={recurrenceRule}   setRecurrenceRule={setRecurrenceRule}
            timezone={timezone}               setTimezone={setTimezone}
            notifyOnCompletion={notifyOnCompletion}     setNotifyOnCompletion={setNotifyOnCompletion}
            notificationChannels={notificationChannels} setNotificationChannels={setNotificationChannels}
            saving={saving}
            saved={saved}
            onSave={handleSave}
          />
        }
      />

      {/* Command Palette */}
      <CommandPalette
        open={cmdOpen}
        onClose={() => setCmdOpen(false)}
        activeSkillIds={selectedTools}
        onToggleSkill={toggleTool}
        onSave={handleSave}
        onNavigateBack={() => navigate('/agents')}
        setSystemPrompt={setSystemPrompt}
      />
      <ConfirmScheduledRunModal
        isOpen={scheduledModalOpen}
        onClose={() => setScheduledModalOpen(false)}
        scheduledAt={scheduledConflictRun?.scheduledAt}
        existingRunId={scheduledConflictRun?._id}
        onReplace={handleReplaceSchedule}
        onKeep={handleKeepExisting}
      />
    </>
  );
}
